import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RotateCcw, Trophy, Sparkles } from "lucide-react";
import { QUIZ } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTIONS = [
  {
    q: "Power of compounding works best with…",
    options: [
      "Higher returns over a short time",
      "Time in the market, not timing it",
      "Picking the latest hot fund every year",
      "Borrowing money to invest",
    ],
    answer: 1,
    why: "Compounding rewards consistency over decades, not heroics over months.",
  },
  {
    q: "An SIP (Systematic Investment Plan) is best described as…",
    options: [
      "A loan to buy mutual funds",
      "A fixed-deposit that pays monthly",
      "An automatic monthly investment in a mutual fund",
      "A stock-tip subscription",
    ],
    answer: 2,
    why: "SIPs auto-invest a fixed amount monthly, removing emotion from the decision.",
  },
  {
    q: "If a fund has a 5-year CAGR of 12%, it means…",
    options: [
      "It returned 12% in the last year",
      "It returned 12% each year on average, compounded",
      "It paid a 12% dividend",
      "It is guaranteed to give 12% next year",
    ],
    answer: 1,
    why: "CAGR is the average annual compounded growth — not a guarantee for next year.",
  },
  {
    q: "Diversification primarily helps to…",
    options: [
      "Maximise returns",
      "Reduce risk by not depending on one asset",
      "Avoid taxes",
      "Time the market better",
    ],
    answer: 1,
    why: "Diversification spreads risk so no single failure derails the entire portfolio.",
  },
  {
    q: "When markets crash by 20%, the smartest move is usually to…",
    options: [
      "Panic and sell everything",
      "Stop your SIP immediately",
      "Continue investing — units become cheaper",
      "Borrow more to invest the next day",
    ],
    answer: 2,
    why: "Falling markets let your SIP buy more units. This is exactly when most wealth is built.",
  },
  {
    q: "An advisor's biggest job is to…",
    options: [
      "Predict the market",
      "Coach behaviour and stop emotional decisions",
      "Pick yesterday's best fund",
      "Promise fixed returns",
    ],
    answer: 1,
    why: "Most investor returns are lost to emotional decisions — that's where an advisor truly adds value.",
  },
  {
    q: "Rule of 72: at 12% returns, money roughly doubles every…",
    options: ["3 years", "6 years", "9 years", "15 years"],
    answer: 1,
    why: "72 ÷ 12 = 6. A handy mental shortcut for compounding.",
  },
];

const STAGES = { INTRO: "intro", PLAYING: "playing", RESULT: "result" };

export default function QuizPage() {
  const [stage, setStage] = useState(STAGES.INTRO);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [name, setName] = useState("");

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const total = QUESTIONS.length;
  const current = QUESTIONS[idx];

  const choose = (i) => {
    if (picked !== null) return;
    setPicked(i);
    if (i === current.answer) setScore((s) => s + 1);
  };

  const next = () => {
    if (idx + 1 >= total) {
      setStage(STAGES.RESULT);
    } else {
      setIdx(idx + 1);
      setPicked(null);
    }
  };

  const restart = () => {
    setIdx(0); setPicked(null); setScore(0); setStage(STAGES.INTRO); setName("");
  };

  const submit = async () => {
    try {
      await axios.post(`${API}/quiz/submit`, { name: name || "Anonymous", score, total });
      toast.success("Your score is saved to our leaderboard.");
    } catch (e) {
      toast.error("Could not save score, but here's your result anyway.");
    }
  };

  const pct = Math.round((score / total) * 100);
  const verdict =
    pct >= 85 ? { t: "Money Maestro", d: "You think like a long-term investor. Now teach a friend." } :
    pct >= 60 ? { t: "Solid Foundation", d: "You're on the right track. Let's plug a few gaps together." } :
    pct >= 40 ? { t: "Beginner's Mind", d: "Great that you're learning! An advisor can fast-track you." } :
                { t: "Just Getting Started", d: "Don't sweat it. Money knowledge is a habit, not a gift." };

  return (
    <div className="bg-background min-h-[80vh]">
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-16">
        <span className="text-xs uppercase tracking-[0.25em] text-secondary font-medium inline-flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Finance Quiz
        </span>
        <h1 className="font-serif text-5xl md:text-6xl text-primary mt-3 leading-[1.05] tracking-tight">
          Money I.Q. <em className="not-italic text-secondary">Test.</em>
        </h1>
        <p className="mt-5 text-lg text-foreground/75">7 questions. 2 minutes. No grades, just insight.</p>

        <AnimatePresence mode="wait">
          {stage === STAGES.INTRO && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="mt-10 bg-card rounded-3xl border border-border p-8"
            >
              <p className="text-foreground/75 leading-relaxed">
                Pick your answer. We'll explain the right one. At the end, you'll see your money mindset and where you can grow.
              </p>
              <button
                data-testid={QUIZ.start}
                onClick={() => setStage(STAGES.PLAYING)}
                className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-7 py-3.5 font-medium hover:-translate-y-0.5 transition-transform"
              >
                Begin Quiz
              </button>
            </motion.div>
          )}

          {stage === STAGES.PLAYING && (
            <motion.div
              key={`q-${idx}`}
              initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
              className="mt-10 bg-card rounded-3xl border border-border p-8"
            >
              <div className="flex items-center justify-between text-xs uppercase tracking-widest text-muted-foreground mb-4">
                <span>Question {idx + 1} of {total}</span>
                <span>Score {score}</span>
              </div>
              <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden mb-6">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${((idx) / total) * 100}%` }}
                  transition={{ duration: 0.4 }}
                  className="h-full bg-secondary"
                />
              </div>

              <h3 className="font-serif text-2xl md:text-3xl text-primary leading-tight">{current.q}</h3>

              <ul className="mt-6 space-y-3">
                {current.options.map((opt, i) => {
                  const isCorrect = i === current.answer;
                  const isPicked = picked === i;
                  const showState = picked !== null;
                  const stateClass = !showState
                    ? "border-border hover:border-primary/40 hover:bg-muted/40"
                    : isCorrect
                      ? "border-green-600/50 bg-green-600/5"
                      : isPicked
                        ? "border-destructive/50 bg-destructive/5"
                        : "border-border opacity-60";
                  return (
                    <li key={i}>
                      <button
                        data-testid={`${QUIZ.option}-${i}`}
                        onClick={() => choose(i)}
                        className={`w-full text-left rounded-2xl border-2 p-4 transition-all flex items-center justify-between gap-3 ${stateClass}`}
                      >
                        <span>{opt}</span>
                        {showState && isCorrect && <CheckCircle2 className="w-5 h-5 text-green-600" />}
                        {showState && isPicked && !isCorrect && <XCircle className="w-5 h-5 text-destructive" />}
                      </button>
                    </li>
                  );
                })}
              </ul>

              {picked !== null && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className="mt-5 rounded-xl bg-muted/60 p-4 text-sm text-foreground/80"
                >
                  <strong className="text-primary">Why:</strong> {current.why}
                </motion.div>
              )}

              <div className="mt-8 flex justify-end">
                <button
                  data-testid={QUIZ.next}
                  disabled={picked === null}
                  onClick={next}
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-7 py-3 font-medium disabled:opacity-40 hover:-translate-y-0.5 transition-transform"
                >
                  {idx + 1 >= total ? "See Result" : "Next"}
                </button>
              </div>
            </motion.div>
          )}

          {stage === STAGES.RESULT && (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
              className="mt-10 bg-card rounded-3xl border border-border p-8 text-center"
              data-testid={QUIZ.result}
            >
              <div className="w-16 h-16 mx-auto rounded-full bg-secondary/15 text-secondary grid place-items-center">
                <Trophy className="w-7 h-7" />
              </div>
              <h2 className="font-serif text-3xl md:text-4xl text-primary mt-5">{verdict.t}</h2>
              <p className="text-foreground/70 mt-2">{verdict.d}</p>

              <div className="mt-8 inline-flex items-baseline gap-2">
                <span className="font-serif text-6xl text-primary">{score}</span>
                <span className="text-2xl text-muted-foreground">/ {total}</span>
              </div>
              <div className="text-sm text-secondary uppercase tracking-widest mt-1">{pct}%</div>

              <div className="mt-8 max-w-sm mx-auto">
                <label className="text-xs uppercase tracking-widest text-muted-foreground">Save to leaderboard (optional)</label>
                <div className="mt-2 flex gap-2">
                  <input
                    data-testid={QUIZ.nameInput}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="flex-1 bg-transparent border-b-2 border-border focus:border-primary py-2 outline-none"
                  />
                  <button
                    data-testid={QUIZ.submitName}
                    onClick={submit}
                    className="bg-primary text-primary-foreground rounded-full px-5 py-2 text-sm"
                  >
                    Save
                  </button>
                </div>
              </div>

              <button
                data-testid={QUIZ.restart}
                onClick={restart}
                className="mt-8 inline-flex items-center gap-2 text-secondary font-medium"
              >
                <RotateCcw className="w-4 h-4" /> Take the quiz again
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </div>
  );
}
